import React from "react";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import { Link } from "react-router-dom";
import {
  DeviceMobileCamera,
  WifiHigh,
  ChatsCircle,
  Lightning,
  ShieldCheck,
  Broadcast,
  Cloud,
  Camera,
  Printer,
  Sun,
  ArrowUpRight,
  Star,
  Check,
} from "@phosphor-icons/react";

const CATEGORIES = [
  {
    icon: Broadcast,
    title: "Connectivity & Telecoms",
    blurb:
      "End-to-end connectivity for every site you run — home office to JSE-listed enterprise.",
    items: [
      "Home & Business fibre, plus dedicated internet access (DIA)",
      "LTE, fixed-wireless and VoIP at best-in-market rates",
      "SD-WAN for secure, optimised business-critical apps",
      "Mobile & cellular contracts across Vodacom, Cell C, MTN",
      "APN services for secure private network connectivity",
      "5G solutions for ultra-fast, low-latency applications",
      "Satellite connectivity for remote and rural sites",
    ],
  },
  {
    icon: Cloud,
    title: "Cloud & Business IT",
    blurb:
      "Partnerships with AWS, Azure, Huawei, Google and Zadara — plus the productivity stack on top.",
    items: [
      "Cloud hosting & infrastructure (AWS · Azure · Huawei · Google · Zadara)",
      "Office 365 & Microsoft Teams Calling at competitive rates",
      "Cybersecurity & network protection for data & compliance",
      "Scalable video conferencing for hybrid collaboration",
    ],
  },
  {
    icon: Lightning,
    title: "Advanced Technology & Digital",
    blurb:
      "The tools that turn connectivity into business transformation.",
    items: [
      "Unified Communications & integrated collaboration",
      "Industry-specific IoT for digital transformation",
      "AI dashcams & fleet management analytics",
      "Digital Pen technology for smart documentation",
      "IT consulting & ongoing support",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Security & Facility",
    blurb:
      "Protect your people, your premises and your data with one accountable partner.",
    items: [
      "CCTV surveillance with advanced monitoring & analytics",
      "Access control systems — secure, customisable entry management",
    ],
  },
  {
    icon: Sun,
    title: "Office Automation & Energy",
    blurb:
      "From the copier to the power grid — sustainable, managed, billed once.",
    items: [
      "Managed print, copiers and workflow optimisation",
      "Solar & energy solutions to reduce costs and carbon footprint",
    ],
  },
];

export default function ServicesPage() {
  return (
    <PublicLayout>
      <Seo
        title="Solutions & Services"
        description="Connectivity, cloud, IT, security, automation and energy services for South African business — from MTN, Vodacom, Cell C, Vox and Telkom."
        path="/services"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 overflow-hidden text-center" data-testid="services-page">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[90vw] h-[320px] rainbow-swoosh pointer-events-none" />
        <InfinityMotif variant="wireframe" className="absolute top-2 left-1/2 -translate-x-1/2 w-[1100px] max-w-[120vw] h-auto" opacity={0.14} />
        <div className="relative max-w-[1000px] mx-auto px-6">
          <div className="flex justify-center mb-7">
            <Star size={24} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />
          <div className="eyebrow mb-5">Solutions</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98]">
            Beyond connectivity —<br />
            <span className="rainbow-text">a full stack.</span>
          </h1>
          <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto">
            Infinity Data &amp; Mobile Solutions is a full-spectrum ICT partner. From a single SIM to
            multi-site fibre, cloud hosting, IoT and solar — we negotiate, deploy, monitor and
            manage your entire technology stack.
          </p>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      {/* Category stack */}
      <section className="py-28">
        <div className="max-w-[1180px] mx-auto px-6 space-y-24">
          {CATEGORIES.map((cat, idx) => {
            const Icon = cat.icon;
            const flip = idx % 2 === 1;
            return (
              <div
                key={cat.title}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${flip ? "lg:flex-row-reverse" : ""}`}
                data-testid={`service-category-${idx}`}
              >
                <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-square rounded-[40px] overflow-hidden bg-gradient-to-br from-[#141e32] via-[#1c2942] to-[#070b14] flex items-center justify-center">
                    <div className="absolute inset-10 rounded-full opacity-70 rainbow-swoosh" />
                    <div className="relative w-28 h-28 rounded-full bg-[#141e32] flex items-center justify-center shadow-[0_20px_40px_-20px_rgba(11,17,32,0.25)]">
                      <Icon size={48} weight="duotone" className="text-[#f5ecd9]" />
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                  <div className="eyebrow mb-4">N° {String(idx + 1).padStart(2, "0")}</div>
                  <h2 className="font-display text-4xl lg:text-5xl text-[#f5ecd9] leading-[1.02]">{cat.title}</h2>
                  <p className="mt-6 text-lg text-[#a8b2cc] leading-relaxed">{cat.blurb}</p>
                  <ul className="mt-6 space-y-3">
                    {cat.items.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#f5ecd9]">
                        <Check size={20} weight="bold" className="text-[#af3fa6] mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-[#141e32] text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 rainbow-bar rainbow-bar-xl" />
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <div className="eyebrow mb-5" style={{ color: "#dda632" }}>Not sure which fits?</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05]">
            Let's audit your stack — <span className="rainbow-text">free.</span>
          </h2>
          <p className="mt-6 text-lg text-[#a8b2cc]">
            50+ years of combined ICT experience between our directors. Send us your current bills
            — we'll tell you exactly where to save.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#f5ecd9] text-[#070b14] rounded-full px-7 py-4 font-mono-accent text-[0.78rem] tracking-[0.22em] uppercase font-semibold hover:bg-[#dda632]" data-testid="services-cta">
              Book a Free Audit <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
